import React, { useEffect } from "react"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import FormSelect from "Components/form/formSelect"
import FormMinMax from "Components/form/formMinMax"
import { Form } from "antd"
import { useTranslation } from "react-i18next"
import { getTableFilterObjectStore } from "Shared/hooks/table.hooks"
import { createDynamicListWithLabel, createYesOrNoOption, getDynamicLocationList, getDynamicTeamList, isAdmin, isApprover } from "Shared/utils/filter.utils"
import { FilterFormInterface } from "Components/filter/filterTypes"
import { RolesEnum } from "@finway-group/shared/lib/models/user/roles.enum"
import { useLoggedInEmployeeProfile } from "Shared/hooks/user.hooks"
import { EmployeeFilter, Team } from "@finway-group/shared/lib/models"
import { generateRules } from "./rules/employeeFilter.rules"
import { FormSelectOption } from "Components/form/form.types"

const adjustFilterObject = (filterObject: EmployeeFilter): EmployeeFilter => ({
    ...filterObject,
    minExpenses: filterObject.minExpenses ? filterObject.minExpenses : undefined,
    maxExpenses: filterObject.maxExpenses ? filterObject.maxExpenses : undefined,
})

const EmployeeFilterForm: React.FC<FilterFormInterface> = ({ table, formInstance, options, onSubmit }) => {
    const { t } = useTranslation()
    const tableFilterObject = getTableFilterObjectStore<EmployeeFilter>(table)
    const [employeeFilter, setEmployeeFilter] = useStateIfMounted(adjustFilterObject(tableFilterObject))

    const rules = generateRules(formInstance)
    const loggedInUser = useLoggedInEmployeeProfile()

    useEffect(() => {
        formInstance.resetFields()
        setEmployeeFilter(adjustFilterObject(tableFilterObject))
    }, [])

    const yesOrNoOptions = createYesOrNoOption()
    const rolesList: FormSelectOption[] = createDynamicListWithLabel("input", [
        { param: "employee.roles.employee", value: RolesEnum.EMPLOYEE },
        { param: "employee.roles.approver", value: RolesEnum.APPROVER },
        { param: "employee.roles.admin", value: RolesEnum.ADMIN },
    ])

    return (
        <Form id="employeeFilterForm" form={formInstance} onFinish={onSubmit}>
            <FormSelect
                name="location"
                header={t("input:employee.location")}
                initialValue={employeeFilter.location}
                options={getDynamicLocationList(options.locations as string[])}
                isCollapse
                showSearch
                fallback
            />

            {(isApprover(loggedInUser) || isAdmin(loggedInUser)) && (
                <FormSelect
                    name="team"
                    header={t("input:filter_form.team")}
                    initialValue={employeeFilter.team}
                    options={getDynamicTeamList(options.teams as Team[])}
                    isCollapse
                    showSearch
                    fallback
                />
            )}

            {isAdmin(loggedInUser) && (
                <>
                    <FormSelect name="role" header={t("input:filter_form.role")} initialValue={employeeFilter.role} options={rolesList} isCollapse fallback />

                    <FormMinMax
                        header={t("input:filter_form.expenses_so_far")}
                        inputType="number"
                        min={{
                            name: "minExpenses",
                            label: t("input:employee_filter.min_expenses"),
                            placeholder: t("placeholder:expense_filter.min_amount"),
                            onChange: () => formInstance.validateFields(["minExpenses"]),
                            initialValue: employeeFilter.minExpenses,
                            rules: rules.minExpenses,
                        }}
                        max={{
                            name: "maxExpenses",
                            label: t("input:employee_filter.max_expenses"),
                            placeholder: t("placeholder:expense_filter.max_amount"),
                            onChange: () => formInstance.validateFields(["maxExpenses"]),
                            initialValue: employeeFilter.maxExpenses,
                            rules: rules.maxExpenses,
                        }}
                    />

                    {/* TODO: Add filter support for paymentDataAdded once stripe is setup
                    <FormSelect 
                        name="paymentDataAdded"
                        header={t("input:filter_form.payment_data_added")}
                        initialValue={employeeFilter.paymentDataAdded}
                        filterOption={doFilterSelect}
                        notSetOption={notSetOption}
                        options={yesOrNoOptions}
                    />
                    */}

                    <FormSelect
                        name="cardProgramEnrolled"
                        header={t("input:filter_form.card_program_enrolled")}
                        initialValue={employeeFilter.cardProgramEnrolled}
                        options={yesOrNoOptions}
                        isCollapse
                        fallback
                    />

                    <FormSelect
                        name="superiorSet"
                        header={t("input:filter_form.superior_set")}
                        initialValue={employeeFilter.superiorSet}
                        options={yesOrNoOptions}
                        isCollapse
                        fallback
                    />

                    <FormSelect
                        name="phoneNumberVerified"
                        header={t("input:filter_form.phone_number_verified")}
                        initialValue={employeeFilter.phoneNumberVerified}
                        options={yesOrNoOptions}
                        isCollapse
                        fallback
                    />

                    <FormSelect
                        name="tfaEnabled"
                        header={t("input:filter_form.tfa_enabled")}
                        initialValue={employeeFilter.tfaEnabled}
                        options={yesOrNoOptions}
                        isCollapse
                        fallback
                    />
                </>
            )}
        </Form>
    )
}

export default EmployeeFilterForm
